import React from "react"
import { graphql } from "gatsby"
import { Box } from "@theme-ui/components"
import Layout from "../components/layout"
import { getHomePath } from "../utils/path"
import { HelmetDatoCms } from "gatsby-source-datocms"
import LatestArticles from "../components/latestArticles"
import { useFavicon } from "../hooks/useFavicon"
import HomeHero from "../components/homeHero"
import ImageAndText from "../components/blocks/imageAndText"
import TitleAndBody from "../components/blocks/titleAndBody"
import DoubleImages from "../components/blocks/doubleImages"
import ContactSection from "../components/blocks/contactSection"
import ReferenceList from "../templates/referenceList"
import ModulesSection from "../components/blocks/modulesSection"
import ContactForm from "../components/blocks/contactForm"
import { i18nContext } from "../context/i18nContext"

const Home = ({
  data: { page, site, articles, contactSection, references },
}) => {
  const favicon = useFavicon().site.faviconMetaTags

  const i18nPaths = site.locales.map(locale => {
    return {
      locale: locale,
      value: getHomePath(locale),
    }
  })

  return (
    <Layout locale={page.locale} i18nPaths={i18nPaths}>
      <HelmetDatoCms seo={page.seoMetaTags} favicon={favicon}>
        <html lang={page.locale} />
      </HelmetDatoCms>
      <HomeHero page={page} />
      {page.content.map(block => (
        <Box as="section" key={block.id}>
          {block.model.apiKey === "title_and_body" && (
            <TitleAndBody block={block} />
          )}
          {block.model.apiKey === "image_and_text" && (
            <ImageAndText block={block} />
          )}
          {block.model.apiKey === "double_image" && (
            <DoubleImages images={block.images} />
          )}
          {block.model.apiKey === "reference_collection" && (
            <ReferenceList
              block={{ references: references.nodes }}
              subtitle={block.subtitle}
              anchorText={block.anchorText}
            />
          )}
          {block.model.apiKey === "modules_section" && (
            <ModulesSection block={block} />
          )}
        </Box>
      ))}
      {articles.nodes.length > 0 && (
        <LatestArticles articles={articles.nodes} />
      )}
      {/* {contactSection && <ContactSection data={contactSection.nodes[0]} />} */}
      
      <i18nContext.Consumer>
        {t => (
          <Box sx={{ backgroundColor: "#fafafa" }}>
            <ContactForm
              block={{
                title: t.contactForm.title,
                subtitle: t.contactForm.subtitle,
                alternateLayout: true,
                contactPage: true,
              }}
            />
          </Box>
        )}
      </i18nContext.Consumer>
    </Layout>
  )
}

export default Home

export const query = graphql`
  query HomeQuery($id: String!, $locale: String!) {
    contactSection: allDatoCmsContactSection(
      filter: { locale: { eq: $locale } }
    ) {
      nodes {
        title
        subtitle
        body {
          links {
            __typename
            ... on DatoCmsInternalLink {
              id: originalId
              anchor
              locale
              model {
                apiKey
              }
              link {
                ... on DatoCmsBlogPage {
                  ...BlogDetails
                }
                ... on DatoCmsPage {
                  ...PageDetails
                  ...PageTreeParent
                  ...AllSlugLocales
                }
                ... on DatoCmsArticle {
                  ...ArticleDetails
                  ...ArticleAllSlugLocales
                }
                ... on DatoCmsArticleCategory {
                  ...ArticleCategoryDetails
                  ...ArticleCategoryAllSlugLocales
                }
              }
            }
          }
          value
        }
      }
    }

    references: allDatoCmsReference(
      sort: { fields: position, order: ASC }
      filter: { locale: { eq: $locale }, hasPage: { eq: true } }
    ) {
      nodes {
        id
        title
        subtitle
        position
        locale
        logo {
          standard: gatsbyImageData(
            width: 400
            placeholder: BLURRED
            forceBlurhash: false
          )
        }
        slug
        _allSlugLocales {
          value
          locale
        }
      }
    }

    page: datoCmsHomePage(id: { eq: $id }) {
      id
      title
      herotext
      heroImage {
        url
        gatsbyImageData(width: 1920, placeholder: BLURRED, forceBlurhash: false)
      }
      content {
        ... on DatoCmsModulesSection {
          id
          image {
            gatsbyImageData(
              width: 1920
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          modules {
            ... on DatoCmsPage {
              title
              locale
              shortDescription {
                value
              }
              icon {
                url
                gatsbyImageData(
                  width: 1920
                  placeholder: BLURRED
                  forceBlurhash: false
                )
              }
              slug
              ...PageDetails
              ...PageTreeParent
              ...AllSlugLocales
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsReferenceCollection {
          id
          subtitle
          anchorText
          model {
            apiKey
          }
        }
        ... on DatoCmsDoubleImage {
          id
          images {
            gatsbyImageData(
              width: 1920
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsTitleAndBody {
          id
          background
          content {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          align
          model {
            apiKey
          }
        }
        ... on DatoCmsImageAndText {
          id
          content {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          image {
            gatsbyImageData(
              width: 1920
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          rightAligned
          fullBackground
          model {
            apiKey
          }
        }
      }
      locale
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }
    articles: allDatoCmsArticle(
      sort: { fields: meta___firstPublishedAt, order: DESC }
      filter: {
        category: { originalId: { eq: "A_D_owA8S-6LPp_E7ih0QQ" } }
        slug: { ne: null }
        locale: { eq: $locale }
      }
      limit: 6
    ) {
      nodes {
        ...ArticleDetails
        ...ArticleAllSlugLocales
        ...ArticleMeta
      }
    }
    site: datoCmsSite {
      locales
    }
  }
`
